import { Input } from "components/Form/FormikControls";
import { useField } from "formik";
import { useEffect } from "react";
import React from "react";
import { useSelector } from "react-redux";
import { getBusinessById } from "selectors";
import { TableCell, TableRow } from "@material-ui/core";
import styled from "styled-components/macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getSideLoadedBusinessMappableOptionsById } from "selectors/sideLoadedBusinesses";
import { SelectField } from "components/Form/FormikControls";
import { useState } from "react";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { FieldConfiguration } from "./fieldConfigurations";
import { getSideLoadedBusinessById } from "selectors/sideLoadedBusinesses";
import { Row } from "components/Layout";

export interface SideLoadedFieldRowProps {
  configuration: FieldConfiguration;
  businessId?: string;
  recordId: string;
}

const TableRowStatus = {
  ValidAndMapped: "ValidAndMapped",
  InvalidAndMapped: "InvalidAndMapped",
  UnMapped: "UnMapped",
};

export const ActiveTableRow = styled(TableRow)`
  background-color: ${({ status, theme }) => {
    switch (status) {
      case TableRowStatus.ValidAndMapped:
        return theme.colors.lightGreen;
      case TableRowStatus.InvalidAndMapped:
        return theme.colors.error;
      case TableRowStatus.UnMapped:
        return theme.colors.gray85;
    }
  }};
`;

interface MappableFieldSelectorProps {
  name: string;
  recordId: string;
}

function MappableFieldSelector({ name, recordId }: MappableFieldSelectorProps) {
  const options = useSelector(
    getSideLoadedBusinessMappableOptionsById(recordId),
  );
  return (
    <SelectField
      name={name}
      options={options}
      placeholder="Not mapped..."
      isClearable
    />
  );
}

function MappableFieldValueDisplay({
  mappableFieldValue,
  valueFieldName,
}: {
  mappableFieldValue: string;
  valueFieldName: string;
}) {
  const [isEditing, setIsEditing] = useState(false);

  const valueField = useField(valueFieldName)[0];

  if (mappableFieldValue === null) {
    return null;
  }
  if (isEditing) {
    return <Input name={valueFieldName} disableAutoComplete />;
  }

  return (
    <span onClick={() => setIsEditing(true)}>
      <FontAwesomeIcon icon={faPencil} />
      &nbsp;{valueField.value}
    </span>
  );
}

export function SideLoadedFieldRow({
  configuration,
  businessId,
  recordId,
}: SideLoadedFieldRowProps) {
  const existingBusiness = useSelector(getBusinessById(businessId)) || {};
  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  const valueFieldName = `Value__${configuration.value}`;
  const mappableFieldName = `MappableField__${configuration.value}`;

  const valueFieldElements = useField(valueFieldName);
  const valueMeta = valueFieldElements[1];
  const valueFieldHelpers = valueFieldElements[2];
  const mappableFieldField = useField(mappableFieldName)[0];
  const mappableFieldValue = mappableFieldField.value;

  const setValueFieldValue = valueFieldHelpers.setValue;

  let rowStatus = TableRowStatus.UnMapped;
  if (mappableFieldField.value !== null) {
    if (valueMeta.error) {
      rowStatus = TableRowStatus.InvalidAndMapped;
    } else {
      rowStatus = TableRowStatus.ValidAndMapped;
    }
  }

  useEffect(() => {
    if (mappableFieldValue === null) {
      setValueFieldValue("");
    } else {
      setValueFieldValue(sideLoadedBusiness.rawData[mappableFieldValue]);
    }
  }, [mappableFieldValue, sideLoadedBusiness, setValueFieldValue]);

  const existingValue =
    (configuration.businessValueGetter &&
      existingBusiness &&
      configuration.businessValueGetter(existingBusiness)) ||
    "-";
  return (
    <>
      {configuration.group && (
        <TableRow>
          <TableCell colSpan={2}>
            <Row marginLeft={4}>
              <h2>{configuration.group}</h2>
            </Row>
          </TableCell>
        </TableRow>
      )}

      <ActiveTableRow status={rowStatus}>
        <TableCell>
          <strong>{configuration.label}</strong>
          <br />
          <i>{existingValue}</i>
        </TableCell>
        <TableCell>
          <MappableFieldSelector name={mappableFieldName} recordId={recordId} />
          <MappableFieldValueDisplay
            mappableFieldValue={mappableFieldValue}
            valueFieldName={valueFieldName}
          />
        </TableCell>
      </ActiveTableRow>
    </>
  );
}
